import React, { useContext, useState, useMemo } from "react";
import axios from "axios";
import {
  Avatar,
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import { OrderContext } from "../../contexts/OrderContext";
import { saveAs } from "file-saver";
import edit_user from "../../assets/img/icons/edit_user.png";
import delete_user from "../../assets/img/icons/delete_user.png";

const PeopleList = () => {
  const { storeData, fetchStoreData } = useContext(OrderContext);
  const people = [
    ...(storeData.customers || []),
    ...(storeData.karigars || []),
  ];

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [selectedPersonIndex, setSelectedPersonIndex] = useState(null);

  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentPerson, setCurrentPerson] = useState(null);
  const [editedName, setEditedName] = useState("");
  const [editedOtp, setEditedOtp] = useState("");
  const [editedMobile, setEditedMobile] = useState("");
  const [editedStatus, setEditedStatus] = useState("");
  const mfr_id = localStorage.getItem("id");

  const itemsPerPage = 10;

  const filteredPeople = useMemo(() => {
    return people.filter(
      (person) =>
        person.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        person.mobile?.includes(searchTerm)
    );
  }, [searchTerm, people]);

  const totalPages = Math.ceil(filteredPeople.length / itemsPerPage);
  const paginatedPeople = filteredPeople.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  const handleExportCSV = () => {
    const csvRows = [
      ["Role", "Name", "Mobile"],
      ...filteredPeople.map((person) => [
        person.role ||
          (storeData.karigars.includes(person) ? "Karigar" : "Customer"),
        person.name,
        person.mobile,
      ]),
    ];
    const csvContent = csvRows.map((row) => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    saveAs(blob, "people_list.csv");
  };

  const handleEdit = (person) => {
    setCurrentPerson(person);
    setEditedName(person.name);
    setEditedMobile(person.mobile);
    setEditedOtp(person.otp || "");
    setEditedStatus(
      person.status !== undefined && person.status !== null
        ? String(person.status)
        : ""
    );

    setOpenEditDialog(true);
  };

  const handleDelete = (person) => {
    setCurrentPerson(person);
    setOpenDeleteDialog(true);
  };

  const handleEditSave = async () => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/fetchorder/useredit`,
        {
          mobile: currentPerson.mobile,
          name: editedName,
          new_mobile: editedMobile,
          otp: Number(editedOtp),
          status: Number(editedStatus),
          mfr_id,
        }
      );
      setOpenEditDialog(false);
      fetchStoreData();
    } catch (error) {
      console.error("Error updating user:", error);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/api/fetchorder/userdelete`,
        {
          data: {
            mobile: currentPerson.mobile,
          },
        }
      );
      console.log(response.data.message);
      setOpenDeleteDialog(false);
      fetchStoreData();
    } catch (error) {
      console.error("Error deleting user:", error);
    }
  };

  return (
    <Box p={2}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2 }}>
        <TextField
          variant="outlined"
          label="Search by name or mobile"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(1);
            setSelectedPersonIndex(null);
          }}
        />
        <Button
          variant="contained"
          onClick={handleExportCSV}
          sx={{
            backgroundColor: "#0D7377",
            "&:hover": { backgroundColor: "#0A5C60" },
          }}
        >
          Export to CSV
        </Button>
      </Box>

      {paginatedPeople.map((person, index) => {
        const actualIndex = (page - 1) * itemsPerPage + index;
        const isSelected = selectedPersonIndex === actualIndex;

        return (
          <React.Fragment key={index}>
            <Paper
              onClick={() =>
                setSelectedPersonIndex(isSelected ? null : actualIndex)
              }
              elevation={3}
              sx={{
                display: "flex",
                alignItems: "center",
                padding: 2,
                marginBottom: 1,
                gap: 2,
                borderRadius: 2,
                cursor: "pointer",
                backgroundColor: isSelected ? "#f3f3f3" : "white",
              }}
            >
              <Avatar sx={{ bgcolor: "#0A4A4D" }}>
                {person.name?.[0]?.toUpperCase() || "?"}
              </Avatar>
              <Box>
                <Typography variant="caption" sx={{ color: "gray" }}>
                  {person.role ||
                    (storeData.karigars.includes(person)
                      ? "Karigar"
                      : "Customer")}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                  {person.name}
                </Typography>
                <Typography variant="body2" sx={{ color: "#555" }}>
                  {person.mobile}
                </Typography>
              </Box>
            </Paper>

            {isSelected && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  mb: 1,
                  gap: 1,
                }}
              >
                <Box
                  component="div"
                  onClick={() => {
                    handleEdit(person);
                  }}
                  sx={{
                    width: 50,
                    height: 50,
                    backgroundColor: "#0D7377", // light indigo
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 1,
                    cursor: "pointer",
                    boxShadow: 1,
                  }}
                >
                  <img src={edit_user} alt="Edit" width={20} height={20} />
                </Box>

                <Box
                  component="div"
                  onClick={() => handleDelete(person)}
                  sx={{
                    width: 50,
                    height: 50,
                    backgroundColor: "#f87171", // light red
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 1,
                    cursor: "pointer",
                    boxShadow: 1,
                  }}
                >
                  <img src={delete_user} alt="Delete" width={20} height={20} />
                </Box>
              </Box>
            )}
          </React.Fragment>
        );
      })}

      {totalPages > 1 && (
        <Pagination
          count={totalPages}
          page={page}
          onChange={(e, value) => {
            setPage(value);
            setSelectedPersonIndex(null);
          }}
          color="primary"
          sx={{ mt: 3, mb: 8 }}
        />
      )}

      {/* Edit Dialog */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)}>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent sx={{ minWidth: 300 }}>
          <TextField
            fullWidth
            margin="dense"
            label="Name"
            value={editedName}
            onChange={(e) => setEditedName(e.target.value)}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Mobile"
            value={editedMobile}
            onChange={(e) => setEditedMobile(e.target.value)}
          />
          <TextField
            fullWidth
            margin="dense"
            label="otp"
            value={editedOtp}
            onChange={(e) => setEditedOtp(e.target.value)}
          />
          <FormControl margin="dense" fullWidth variant="outlined">
            <InputLabel shrink>Status</InputLabel>
            <Select
              label="Status"
              name="status"
              value={editedStatus}
              onChange={(e) => setEditedStatus(e.target.value)}
            >
              <MenuItem value="1">Active</MenuItem>
              <MenuItem value="0">Inactive</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleEditSave} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{" "}
            <strong>{currentPerson?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PeopleList;
